/* eslint-disable no-param-reassign */
// Is because of playerInstance.volume, which has a setter. so it's okay
import React, { SetStateAction, useEffect } from 'react';
import { Interactable, Icon, ActivityIndicator } from 'LocalGluons';
import { View, StyleSheet } from 'react-native';
import { Device } from 'DeviceUtils';
import { PlayerManager } from '@24i/react-native-player';
import { ISeekedEvent } from '@24i/player-base';
import { useStore } from 'Store';
import { PlayerData, StreamType, StyleMap } from '../../../../../types';
import VolumeControls from './components/VolumeControls';
import defaultStyles from './styles';
import { playerModalsEnum } from '../PlayerModal';
import { useChromecastContext } from 'LocalGluons/components/ChromecastContext';

const DIVIDER_MARGIN = Device.isTablet ? 0 : 20;

const BUTTON_HITSLOP = { top: 35, bottom: 35, left: 35, right: 35 };
const NOT_ACTIVE_OPACITY = 0.3;

interface PlayerControlsProps {
    item: PlayerData['item'];
    style?: StyleMap | StyleMap[];
    isPaused: boolean;
    onPlay: () => void;
    onPause: () => void;
    playerInstance: PlayerManager;
    streamType: StreamType;
    currentTime?: number;
    volume: number;
    setVolume: (newVolume: number) => Promise<void>;
    onNextPress: (() => void) | undefined;
    onPreviousPress: (() => void) | undefined;
    hasArchive: boolean;
    setAlertMessage: React.Dispatch<SetStateAction<string>>;
    showActivityIndicator: boolean;
    modalType: playerModalsEnum | null;
    setModalType: (type: playerModalsEnum | null) => void;
    isModalBinge: boolean;
    setLiveLoading: React.Dispatch<React.SetStateAction<boolean>>;
    alertTimeout: React.MutableRefObject<NodeJS.Timeout>;
    onSeekBackward: () => void;
    onSeekForward: () => void;
    showAlertForSeekingForbiden: ((channelId?: number) => void) | (() => void);
    canSeekBackwards: boolean;
    canSeekForwards: boolean;
    lowLatency: boolean;
}

const PlayerControls = ({
    item,
    style,
    isPaused,
    onPlay,
    onPause,
    playerInstance,
    streamType,
    currentTime,
    volume: currentVolume,
    setVolume,
    onNextPress,
    onPreviousPress,
    hasArchive,
    setAlertMessage,
    showActivityIndicator = false,
    modalType,
    setModalType,
    isModalBinge,
    setLiveLoading,
    alertTimeout,
    onSeekBackward,
    onSeekForward,
    showAlertForSeekingForbiden,
    canSeekBackwards,
    canSeekForwards,
    lowLatency
}: PlayerControlsProps): JSX.Element => {
    const { isGracePeriod } = useStore();
    const { client } = useChromecastContext();

    useEffect(() => {
        const shouldShowAlert = (event: ISeekedEvent) => {
            if (lowLatency && event?.seekingRestricted || !isGracePeriod && (event.adBreakOffset || event.seekingRestricted))
                showAlertForSeekingForbiden();
        };
        playerInstance?.addEventListener('seeked', shouldShowAlert);

        return () => {
            playerInstance?.removeEventListener('seeked', shouldShowAlert);
        };
    }, [playerInstance, isGracePeriod, lowLatency]);

    const onVolumeDecreasePress = async () => {
        await setVolume(Math.max(0, currentVolume - 0.1));
    };

    const onVolumeRaisePress = async () => {
        await setVolume(Math.min(1, currentVolume + 0.1));
    };

    const setVolumeByValue = async (value: number) => {
        await setVolume(Math.min(1, Math.max(0, value)));
    };

    const onPreviousPressEnabled = lowLatency ? false : (onPreviousPress
        && (!showActivityIndicator || isModalBinge));
    const onNextPressEnabled = lowLatency ? false : (onNextPress
        && (!showActivityIndicator || isModalBinge));

    return (
        <View style={[defaultStyles.container, style]}>
            <View
                style={StyleSheet.flatten([
                    defaultStyles.controlsContainer,
                    hasArchive && defaultStyles.hasArchiveControls
                ])}
            >
                {hasArchive && (
                    <Interactable onPress={onPreviousPressEnabled ? onPreviousPress : null} testID="previous_asset" style={defaultStyles.interactable}>
                        <Icon
                            hitSlop={BUTTON_HITSLOP}
                            name="player_previous"
                            style={{
                                ...defaultStyles.nextPrevIcon,
                                position: 'relative',
                                opacity: onPreviousPressEnabled ?
                                    1 : NOT_ACTIVE_OPACITY,
                                marginRight: 20
                            }}
                        />
                    </Interactable>
                )}
                <Interactable onPress={lowLatency ? showAlertForSeekingForbiden : onSeekBackward} testID="-30sec_button" style={defaultStyles.interactable}>
                    <View style={Device.isTabletOrWeb ? defaultStyles.spacing : {}}>
                        <Icon
                            hitSlop={BUTTON_HITSLOP}
                            name="player_rewind_30"
                            style={{
                                ...defaultStyles.icon,
                                position: 'relative',
                                opacity: lowLatency ? NOT_ACTIVE_OPACITY : ((!showActivityIndicator || isModalBinge) ?
                                    1 : NOT_ACTIVE_OPACITY)
                                // opacity: canSeekBackwards ? 1 : NOT_ACTIVE_OPACITY
                            }}
                        />
                    </View>
                </Interactable>
                <View style={[defaultStyles.playPauseIcon]}>
                    {showActivityIndicator ?
                        <ActivityIndicator
                            color='white'
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            style={[defaultStyles.playPauseIcon, Device.isIos ? { transform: [{ scaleX: 1.5 }, { scaleY: 1.5 }] } : {}]} size={Device.isAndroid ? 50 : 'large'}
                        />
                        : <Icon
                            hitSlop={BUTTON_HITSLOP}
                            style={[defaultStyles.playPauseIcon, {
                                opacity: lowLatency ? NOT_ACTIVE_OPACITY : 1
                            }]}
                            onPress={lowLatency ? showAlertForSeekingForbiden : (isPaused ? onPlay : onPause)}
                            name={isPaused ? 'play_arrow_player' : 'player_pause'}
                            testID={isPaused ? 'play_button' : 'pause_button'}
                        />}
                </View>
                <Interactable onPress={lowLatency ? showAlertForSeekingForbiden : onSeekForward} style={defaultStyles.interactable}>
                    <View style={Device.isTabletOrWeb ? defaultStyles.spacing : {}}>
                        <Icon
                            hitSlop={BUTTON_HITSLOP}
                            name="player_forward_30"
                            style={{
                                ...defaultStyles.icon,
                                position: 'relative',
                                opacity: client ? canSeekForwards && !showActivityIndicator ? 1 : NOT_ACTIVE_OPACITY :
                                    lowLatency ? NOT_ACTIVE_OPACITY : ((canSeekForwards ||
                                        (streamType === 'LIVE' &&
                                            playerInstance?.live === false && (playerInstance?.getUTCTimestamp(playerInstance?.currentTime).getTime() < Date.now() - 30000)
                                        ))
                                        && !showActivityIndicator ? 1 : NOT_ACTIVE_OPACITY)
                            }}
                            testID="+30sec_button"
                        />
                    </View>

                </Interactable>
                {hasArchive && (
                    <Interactable onPress={onNextPressEnabled ? onNextPress : null} testID="next_asset" style={defaultStyles.interactable}>
                        <Icon
                            hitSlop={BUTTON_HITSLOP}
                            name="player_next"
                            style={{
                                ...defaultStyles.nextPrevIcon,
                                position: 'relative',
                                opacity: onNextPressEnabled ?
                                    1 : NOT_ACTIVE_OPACITY
                            }}
                        />
                    </Interactable>
                )}
            </View>
            <View style={defaultStyles.volumeContainer}>
                <VolumeControls
                    volume={currentVolume}
                    onVolumeDecreasePress={onVolumeDecreasePress}
                    onVolumeRaisePress={onVolumeRaisePress}
                    setVolumeByValue={setVolumeByValue}
                />
            </View>
        </View>
    );
};

export default PlayerControls;
